import { ELIGIBILITY_STATUS } from '@/types/eligibility';
import { PLANNING_STATUS, PROCEDURE_STATUS } from '@/types/procedures/status';
import { z } from 'zod';
import { ProtocolWithCoordinatesSchema } from '@/api/schema/protocol';
import { UserSnapshotSchema } from '@/api/schema/user';

export const ProcedureType = {
  CLINICAL: 'clinical',
  ASSESSMENT: 'assessment',
} as const;

export enum ProcedureFileType {
  NIFTI = 'NIFTI',
  DICOM_2D = 'DICOM-2D',
  DICOM_3D = 'DICOM-3D',
  OTHER = 'OTHER',
  PROCESSING = 'PROCESSING',
  JSON = 'JSON',
}

export enum Modality {
  CT = 'CT',
  MRI = 'MRI',
  OTHER = 'OTHER',
}

export enum ScanType {
  T1w = 'T1w',
  FGATIR = 'FGATIR',
}

export enum ProcedureFilePlanningStatus {
  IN_PROGRESS = 'in_progress',
  VERIFICATION_BY_RNI = 'verification_by_rni',
  APPROVED = 'approved',
  ARCHIVED = 'archived',
  PROCESSED = 'processed',
}

export enum ImageProcessingJobStatus {
  PENDING = 'PENDING',
  RUNNING = 'RUNNING',
  READY = 'READY',
  FAILED = 'FAILED',
  CANCELED = 'CANCELED',
  ORPHANED_TIMEOUT = 'ORPHANED_TIMEOUT',
}

export enum PipelineIdentifier {
  BULK_FILE_DELETION = 'BULK_FILE_DELETION',
  IMAGE_PREPROCESSING = 'IMAGE_PREPROCESSING',
  THUMBNAIL_GENERATION = 'THUMBNAIL_GENERATION',
}

export const CoordinateDataSchema = z.object({
  ap: z.number(),
  rl: z.number(),
  si: z.number(),
});

const CreatedBySchema = z.object({
  id: z.string(),
  name: z.string().optional().nullable(),
  givenName: z.string().optional().nullable(),
  familyName: z.string().optional().nullable(),
  email: z.string().optional(),
  site: z
    .object({
      id: z.string(),
      name: z.string(),
    })
    .optional()
    .nullable(),
  role: z
    .object({
      code: z.string(),
      name: z.string(),
    })
    .optional()
    .nullable(),
});

const DicomMetadataSchema = z.object({
  patientBirthDate: z.string().nullable(),
  patientId: z.string().nullable(),
  patientName: z.string().nullable(),
  scanDate: z.string().nullable(),
  studyId: z.string().nullable(),
  modality: z.nativeEnum(Modality).optional().nullable(),
  scanType: z.nativeEnum(ScanType).optional().nullable(),
});

const AttributesSchema = z.object({
  fileType: z.nativeEnum(ProcedureFileType),
  seriesInstanceUID: z.string().optional(),
  sopInstanceUID: z.string().optional(),
  hasPixelData: z.boolean().optional(),
  numberOfFrames: z.number().optional(),
  processingJobId: z.string().optional(),
  isMainProcessingFile: z.boolean().optional(),
});

export const FileSchema = z.object({
  id: z.string(),
  name: z.string(),
  thumbnailUrl: z.string().nullable(),
  previewUrl: z.string().nullable(),
  fileUrl: z.string(),
  metadata: DicomMetadataSchema.nullable(),
  createdBy: CreatedBySchema.nullable(),
  createdAt: z.string().optional().nullable(),
  attributes: AttributesSchema.optional().nullable(),
});

export const FilesResponseSchema = z.object({
  items: z.array(FileSchema),
});

export const FileSummarySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  metadata: DicomMetadataSchema.nullable(),
  createdBy: CreatedBySchema.nullable(),
  createdAt: z.string().optional().nullable(),
  attributes: AttributesSchema.optional().nullable(),
});

export const FileSummaryResponseSchema = z.object({
  items: z.array(FileSummarySchema),
});

export const FusedImageSchema = z.object({
  id: z.string(),
  name: z.string(),
  fileUrl: z.string(),
  thumbnailUrl: z.string().nullable(),
  previewUrl: z.string().nullable(),
  attributes: AttributesSchema.optional().nullable(),
  metadata: DicomMetadataSchema.optional().nullable(),
  planningData: z
    .object({
      status: z.nativeEnum(ProcedureFilePlanningStatus),
      createdBy: CreatedBySchema,
    })
    .nullable(),
  createdAt: z.string(),
  createdBy: CreatedBySchema.nullable(),
});

export const ProcessingJobWithFilesSchema = z.object({
  processingJobId: z.string(),
  status: z.nativeEnum(ImageProcessingJobStatus),
  files: z.array(FusedImageSchema),
});

export const ProcessingJobWithFilesResponseSchema = z.object({
  items: z.array(ProcessingJobWithFilesSchema),
});

export const ProcedureListItemSchema = z.object({
  id: z.string(),
  status: z.nativeEnum(PROCEDURE_STATUS),
  procedureDate: z.string().nullable(),
  planningStatus: z.nativeEnum(PLANNING_STATUS),
  patient: z.object({
    id: z.string(),
    humanReadableId: z.string(),
  }),
  subject: z.object({
    id: z.string(),
    humanReadableId: z.string(),
    eligibilityStatus: z.nativeEnum(ELIGIBILITY_STATUS),
  }),
  protocol: z.object({
    id: z.string(),
    name: z.string(),
  }),
  site: z
    .object({
      id: z.string(),
      name: z.string(),
    })
    .optional(),
});

export const ActiveCoordinateSchema = z.object({
  id: z.string(),
  data: CoordinateDataSchema,
  note: z.string(),
  createdAt: z.string(),
  updatedAt: z.string().optional(),
  createdBy: z.object({
    id: z.string(),
    name: z.string(),
  }),
  procedureTarget: z
    .object({
      id: z.string(),
    })
    .optional(),
});

export const ActiveTargetSchema = z.object({
  id: z.string(),
  name: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  activeCoordinate: ActiveCoordinateSchema.optional().nullable(),
  plannedCoordinate: ActiveCoordinateSchema.optional().nullable(),
});

export const ProcedureUserSchema = z.object({
  id: z.string(),
  name: z.string(),
});

export const ProcedureSchema = z.object({
  id: z.string(),
  type: z.nativeEnum(ProcedureType),
  config: z
    .object({
      sonicationPower: z.number().nullable(),
    })
    .optional(),
  procedureDate: z.string().nullable(),
  status: z.nativeEnum(PROCEDURE_STATUS),
  planningStatus: z.nativeEnum(PLANNING_STATUS),
  protocol: ProtocolWithCoordinatesSchema.extend({
    coordinateNames: z.array(z.string()).optional(),
  }),
  patient: z.object({
    id: z.string(),
    eligibilityStatus: z.nativeEnum(ELIGIBILITY_STATUS),
    humanReadableId: z.string(),
  }),
  site: z
    .object({
      id: z.string().uuid(),
      name: z.string(),
    })
    .optional(),
  subject: z.object({
    id: z.string(),
    humanReadableId: z.string(),
  }),
  activeTargets: z.array(ActiveTargetSchema).optional(),
  operator: ProcedureUserSchema.nullish(),
  cueingOperator: ProcedureUserSchema.nullish(),
  isCueingChecklistAccepted: z.boolean(),
  isCockpitChecklistAccepted: z.boolean(),
});

export const CoordinatesHistorySchema = z.array(ActiveCoordinateSchema);

const TargetTagSchema = z.object({
  id: z.string(),
  procedureTarget: z.object({
    id: z.string(),
    name: z.string(),
  }),
  relatedCoordinate: z
    .object({
      id: z.string(),
      data: CoordinateDataSchema,
      note: z.string(),
      createdAt: z.string(),
      updatedAt: z.string().optional(),
      createdBy: CreatedBySchema,
    })
    .nullable(),
});

export const CommentSchema = z.object({
  id: z.string(),
  commentText: z.string(),
  createdAt: z.string(),
  createdBy: CreatedBySchema,
  targetTags: z.array(TargetTagSchema).optional(),
});

export const CommentsListSchema = z.array(CommentSchema);

// Notes
export const ProcedureNoteSchema = z.object({
  id: z.string(),
  text: z.string(),
  cueingBlockName: z.string().nullable(),
  sonicationPassOrder: z.number().nullable(),
  createdAt: z.string(),
  createdBy: CreatedBySchema,
});

export const ProcedureNotesListSchema = z.object({
  items: z.array(ProcedureNoteSchema),
});

// Treatment plan

export const SingleTreatmentCoordinateSchema = z.object({
  targetName: z.string(),
  note: z.string().trim().max(500),
  rl: z.number(),
  ap: z.number(),
  si: z.number(),
});

export const UpdateProcedureInputSchema = z.object({
  sonicationPower: z.number().min(40).max(100).optional(),
  note: z.string().max(1000).nullable().optional(),
  coordinates: z.array(SingleTreatmentCoordinateSchema).optional(),
});

export const GetProcedureFileFramesParametersSchema = z.object({
  fileId: z.string().uuid(),
});

export const GetProcedureFileFramesResponseSchema = z.object({
  frameUrls: z.array(z.string()),
  fileType: z.nativeEnum(ProcedureFileType),
});

export const AnatomicalPositionSchema = z.object({
  ap: z.number(),
  rl: z.number(),
  si: z.number(),
});

export const withSuggested = <T extends z.ZodRawShape>(schema: z.ZodObject<T>) => (
  schema.extend({
    isSuggested: z.boolean(),
  })
);

export const AnatomicalPositionRangeSchema = z.object({
  rlStart: z.number(),
  rlEnd: z.number(),
  apStart: z.number(),
  apEnd: z.number(),
  siStart: z.number(),
  siEnd: z.number(),
});

export const AnatomicalPositionSuggestedSchema = withSuggested(AnatomicalPositionSchema);
export const AnatomicalRangeSuggestedSchema = withSuggested(AnatomicalPositionRangeSchema);

export const MlDataSchema = z.object({
  entries: z.array(AnatomicalPositionSchema),
  isSuggested: z.boolean(),
});

export const PlanningDataSchema = z.object({
  id: z.string(),
  status: z.nativeEnum(ProcedureFilePlanningStatus),
  acData: AnatomicalPositionSuggestedSchema,
  pcData: AnatomicalPositionSuggestedSchema,
  acPcLength: z.string().nullable().optional(),
  mlData: MlDataSchema,
  leftSweetSpot: AnatomicalRangeSuggestedSchema.nullable().optional(),
  leftCenterData: AnatomicalPositionSuggestedSchema,
  rightSweetSpot: AnatomicalRangeSuggestedSchema.nullable().optional(),
  rightCenterData: AnatomicalPositionSuggestedSchema,
  note: z.string().nullable().optional(),
  leftTargetNote: z.string().nullable().optional(),
  rightTargetNote: z.string().nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export enum PlanningDataResetGroup {
  AC_PC_ML = 'ac_pc_ml',
  LEFT_TARGET = 'left_target',
  RIGHT_TARGET = 'right_target',
}

const AcPcHistorySchema = z.object({
  acData: AnatomicalPositionSchema.nullable(),
  pcData: AnatomicalPositionSchema.nullable(),
  mlData: z.array(AnatomicalPositionSchema).nullable(),
});

const SweetSpotHistorySchema = z.object({
  rlStart: z.number(),
  rlEnd: z.number(),
  apStart: z.number(),
  apEnd: z.number(),
  siStart: z.number(),
  siEnd: z.number(),
});

const TargetHistorySchema = z.object({
  sweetSpot: SweetSpotHistorySchema,
  centerData: AnatomicalPositionSchema,
});

export enum PlanningDataChangeSource {
  ALGORITHM = 'algorithm',
  USER = 'user',
  RESET = 'reset',
}

const BaseHistoryEntrySchema = z.object({
  type: z.literal('history'),
  id: z.string(),
  source: z.nativeEnum(PlanningDataChangeSource),
  note: z.string().nullable(),
  changedBy: UserSnapshotSchema.nullable(),
  changedAt: z.string(),
});

const AcPcHistoryEntrySchema = BaseHistoryEntrySchema.extend({
  fieldType: z.literal(PlanningDataResetGroup.AC_PC_ML),
  previousValue: AcPcHistorySchema.nullable(),
  newValue: AcPcHistorySchema,
});

const LeftTargetHistoryEntrySchema = BaseHistoryEntrySchema.extend({
  fieldType: z.literal(PlanningDataResetGroup.LEFT_TARGET),
  previousValue: TargetHistorySchema.nullable(),
  newValue: TargetHistorySchema,
});

const RightTargetHistoryEntrySchema = BaseHistoryEntrySchema.extend({
  fieldType: z.literal(PlanningDataResetGroup.RIGHT_TARGET),
  previousValue: TargetHistorySchema.nullable(),
  newValue: TargetHistorySchema,
});

const HistoryEntrySchema = z.discriminatedUnion('fieldType', [
  AcPcHistoryEntrySchema,
  LeftTargetHistoryEntrySchema,
  RightTargetHistoryEntrySchema,
]);

const StatusEntrySchema = z.object({
  type: z.literal('status'),
  id: z.string(),
  previousValue: z.nativeEnum(ProcedureFilePlanningStatus).nullable(),
  newValue: z.nativeEnum(ProcedureFilePlanningStatus),
  changedBy: UserSnapshotSchema.nullable(),
  changedAt: z.string(),
});

export const PlanningHistoryEntrySchema = z.union([ HistoryEntrySchema, StatusEntrySchema ]);

export const PlanningHistorySchema = z.object({
  items: z.array(PlanningHistoryEntrySchema),
});

export type File = z.infer<typeof FileSchema>;
export type FileSummary = z.infer<typeof FileSummarySchema>;
export type FileSummaryResponse = z.infer<typeof FileSummaryResponseSchema>;
export type FilesResponse = z.infer<typeof FilesResponseSchema>;
export type ProcedureListItem = z.infer<typeof ProcedureListItemSchema>;
export type ProcedureFilesResponse = File[];
export type ActiveCoordinate = z.infer<typeof ActiveCoordinateSchema>;
export type ActiveTarget = z.infer<typeof ActiveTargetSchema>;
export type Procedure = z.infer<typeof ProcedureSchema>;
export type ProcedureUser = z.infer<typeof ProcedureUserSchema>;
export type CoordinatesHistory = z.infer<typeof CoordinatesHistorySchema>;
export type Comment = z.infer<typeof CommentSchema>;
export type CommentsList = z.infer<typeof CommentsListSchema>;
export type TargetTag = z.infer<typeof TargetTagSchema>;
export type ProcedureNote = z.infer<typeof ProcedureNoteSchema>;
export type ProcedureNotesList = z.infer<typeof ProcedureNotesListSchema>;
export type UpdateProcedureInput = z.infer<typeof UpdateProcedureInputSchema>;
export type SingleTreatmentCoordinate = z.infer<
  typeof SingleTreatmentCoordinateSchema
>;
export type GetProcedureFileFramesParameters = z.infer<
  typeof GetProcedureFileFramesParametersSchema
>;
export type GetProcedureFileFramesResponse = z.infer<
  typeof GetProcedureFileFramesResponseSchema
>;
export type DicomMetadata = z.infer<typeof DicomMetadataSchema>;
export type FusedImage = z.infer<typeof FusedImageSchema>;
export type ProcessingJobWithFiles = z.infer<typeof ProcessingJobWithFilesSchema>;
export type ProcessingJobWithFilesResponse = z.infer<
  typeof ProcessingJobWithFilesResponseSchema
>;
export type AnatomicalPosition = z.infer<typeof AnatomicalPositionSchema>;
export type AnatomicalPositionRange = z.infer<typeof AnatomicalPositionRangeSchema>;
export type AnatomicalPositionSuggested = z.infer<typeof AnatomicalPositionSuggestedSchema>;
export type MlData = z.infer<typeof MlDataSchema>;
export type PlanningData = z.infer<typeof PlanningDataSchema>;
export type PlanningHistoryEntry = z.infer<typeof PlanningHistoryEntrySchema>;
export type HistoryEntry = z.infer<typeof HistoryEntrySchema>;
export type StatusEntry = z.infer<typeof StatusEntrySchema>;
export type PlanningHistory = z.infer<typeof PlanningHistorySchema>;
